import { useEffect } from "react";
import { useIntl } from "react-intl";
import { useUIStateContext } from "../../../../contexts/UIStateContext";
import { useUserContext } from "../../../../contexts/UserContext";
import {
  Button,
  TextButton,
  IconRefresh,
  IconChevronLeft,
  BUTTON_VARIANTS,
  BUTTON_SIZES,
  BUTTON_ICON_COLORS,
  IconSync,
  PrimaryButton,
  SecondaryButton,
  IconFileUpload,
  IconAdd,
  PageHeaderTitle,
} from "@cdkglobal/radial";
import {
  useRefetchRecordData,
  useRefetchRowDataList,
  useSaveRecord,
  useARRecordScheduleTypeConversion,
  useLandingPageState,
} from "../../../../hooks";
import { LandingPageHeaderProps } from "../../../../types";
import {
  StyledPageHeaderTitleContainer,
  StyledPageHeaderButtonContainer,
  StyledPageHeaderWrapper,
} from "../../../../utils/styled";
import {
  evaluateSchemaContext,
  isSetupRecordActionsRestricted,
  getPageHeaderTitle,
  getSetupBasedNewEntryText,
} from "../../helpers";
import CopyGroupCodesDialog from "../custom-renderers/group-codes/CopyGroupCodesDialog";
import CopyPOUnitsOfMeasureDialog from "../custom-renderers/po-units-of-measure/CopyPOUnitsOfMeasureDialog";
import { useHeaderFeatureFlags } from "./landingPageHeader.flags";
import {
  buildOnSuccessCallback,
  HeaderHandlerContext,
  handleSave,
  handleCancelAndReset,
  handleNewEntryClick,
  handleConvertARRecordScheduleType,
  handleRecordReset,
  handleConversionARRecordDialogOpen,
} from "./landingPageHeader.handlers";
import { resolveHeaderVisibility } from "./landingPageHeader.visibility";
import ARMigrationDialog from "../table-actions-setup-based/ARMigrationDialog";

export default function LandingPageHeader({
  rowDataReducerState,
  dispatch,
}: Readonly<LandingPageHeaderProps>): JSX.Element {
  const { formatMessage } = useIntl();
  const intl = useIntl();

  const {
    headerLabel,
    setTableTitleInfo,
    setupBaseRequestInfo,
    selected,
    setupListNavInfoByDepartment,
    permissions,
  } = useUserContext();

  const {
    isRecordOpen,
    setIsRecordOpen,
    setRecordPageToast,
    setIsSideSheetOpen,
    setIsFileUploadDialogOpen,
    setLandingPageToast,
    selectedTabSectionKey,
    setSelectedTabSectionKey,
    selectedDashboardTabSectionKey,
    setCurrentPage,
    setSearchValue,
    saveRequestId,
    resetSaveRequestId,
    activeTabIndex,
    setActiveTabIndex,
    activeAccountsReceivableRecordMigrationInfo,
    setActiveAccountsReceivableRecordMigrationInfo,
    convertARScheduleTypeId,
    resetConvertARScheduleTypeId,
  } = useUIStateContext();

  const {
    schema,
    rowData,
    rowDataList,
    columnDefs,
    isReadOnly,
    resetState,
    deletedObjectsList,
  } = rowDataReducerState;

  const { isLoading } = useLandingPageState(rowDataReducerState);

  const setupName = schema.title;
  const isReadOnlyRecord = isReadOnly ?? false;
  const isSetupSelected = selected.setup.value !== "";

  const { setupMode } = evaluateSchemaContext({
    schema,
    activeTabIndex,
    selectedDashboardTabSectionKey,
    isRecordOpen,
  });

  // ---------------------------------------------------------------------------
  // Feature flags
  // ---------------------------------------------------------------------------

  const flags = useHeaderFeatureFlags({ setupName, isReadOnly, rowData });

  // ---------------------------------------------------------------------------
  // Locks & visibility
  // ---------------------------------------------------------------------------

  const isSetupActionsLocked =
    isSetupRecordActionsRestricted(setupName, selectedDashboardTabSectionKey) &&
    flags.isPrivlibFeatureEnabled;

  const visibility = resolveHeaderVisibility({
    isLoading,
    isRecordOpen,
    isReadOnly: isReadOnlyRecord,
    setupMode,
    columnDefs,
    rowData,
    rowDataList,
    selectedTabSectionKey,
    selectedDashboardTabSectionKey,
    rowDataReducerState,
    flags,
    isSetupActionsLocked,
    isSetupSelected,
    activeAccountsReceivableRecordMigrationInfo,
  });

  // ---------------------------------------------------------------------------
  // Query / mutation hooks
  // ---------------------------------------------------------------------------

  const { refetch: refetchRecordData } = useRefetchRecordData({
    rowData,
    setupBaseRequestInfo,
    setIsRecordOpen,
    setLandingPageToast,
    dispatch,
  });

  const { refetch: refetchRowDataList } = useRefetchRowDataList({
    setupBaseRequestInfo,
    headerLabel,
    dispatch,
    setTableTitleInfo,
  });

  // onSuccessCallback only needs setters + refetch — not the mutation triggers
  // themselves, so we can build it before the mutation hooks are called.
  const successCallbackCtx = {
    schema,
    rowData,
    rowDataList,
    rowDataReducerState,
    resetState,
    deletedObjectsList,
    setupName,
    setupMode,
    setupBaseRequestInfo,
    selected,
    selectedTabSectionKey,
    selectedDashboardTabSectionKey,
    dispatch,
    setIsRecordOpen,
    setIsSideSheetOpen,
    setActiveTabIndex,
    setRecordPageToast,
    setLandingPageToast,
    setSelectedTabSectionKey,
    setCurrentPage,
    setSearchValue,
    refetchRecordData,
    refetchRowDataList,
    resetSaveRequestId,
    resetConvertARScheduleTypeId,
  };

  const { mutate: saveSingleRecord } = useSaveRecord({
    onSuccessCallback: (response) => {
      void buildOnSuccessCallback(successCallbackCtx)(response);
    },
  });

  const { mutate: convertARScheduleType } = useARRecordScheduleTypeConversion({
    onSuccessCallback: (response) => {
      void buildOnSuccessCallback(successCallbackCtx)(response);
    },
  });

  // Fully resolved handler context — includes mutation triggers
  const ctx: HeaderHandlerContext = {
    ...successCallbackCtx,
    saveSingleRecord,
    convertARScheduleType,
    activeAccountsReceivableRecordMigrationInfo,
    setActiveAccountsReceivableRecordMigrationInfo,
  };

  // ---------------------------------------------------------------------------
  // Save-on-demand via saveRequestId signal
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (saveRequestId === 0) return;
    handleSave(ctx);
  }, [saveRequestId]);

  // ---------------------------------------------------------------------------
  // Convert AR Schedule Type on-demand via convertARScheduleTypeId signal
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (convertARScheduleTypeId === 0) return;
    handleConvertARRecordScheduleType(ctx);
  }, [convertARScheduleTypeId]);

  // ---------------------------------------------------------------------------
  // i18n strings
  // ---------------------------------------------------------------------------

  const cancelOrCloseText = isReadOnlyRecord
    ? formatMessage({ id: "button.close", defaultMessage: "Close" })
    : formatMessage({ id: "button.cancel", defaultMessage: "Cancel" });

  intl.formatMessage({ id: "cdcm.header.home", defaultMessage: "Setups" });
  intl.formatMessage({ id: "cdcm.header.new", defaultMessage: "New Entry" });
  intl.formatMessage({ id: "cdcm.header.view", defaultMessage: "View Entry" });
  intl.formatMessage({ id: "cdcm.header.edit", defaultMessage: "Edit Entry" });

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <StyledPageHeaderWrapper $hideDivider={isSetupSelected && !isRecordOpen}>
      <StyledPageHeaderTitleContainer>
        {visibility.shouldShowRecordBackToHomeButton && (
          <Button
            id="back-to-home"
            dataTestId="back-to-home"
            text={formatMessage({
              id: "button.backToHome",
              defaultMessage: "Back To Home",
            })}
            icon={<IconChevronLeft />}
            variant={BUTTON_VARIANTS.ICON}
            size={BUTTON_SIZES.SMALL}
            onClick={() => handleCancelAndReset(ctx)}
            iconColor={BUTTON_ICON_COLORS.GRAY}
          />
        )}

        <PageHeaderTitle>
          {getPageHeaderTitle({
            rowDataReducerState,
            isRecordOpen,
            isSetupSelected,
            selected,
            setupListNavInfoByDepartment,
            intl,
          })}
        </PageHeaderTitle>
      </StyledPageHeaderTitleContainer>
      {/* Landing Page Actions */}
      {visibility.shouldShowLandingPageActions && (
        <StyledPageHeaderButtonContainer>
          {flags.isCopyGroupCodesFeatureEnabled && (
            <CopyGroupCodesDialog
              rowDataReducerState={rowDataReducerState}
              dispatch={dispatch}
            />
          )}

          {flags.isCopyPOUnitsOfMeasureFeatureEnabled && (
            <CopyPOUnitsOfMeasureDialog
              rowDataReducerState={rowDataReducerState}
              dispatch={dispatch}
            />
          )}

          {flags.isFileUploadFeatureEnabled &&
            permissions.setupPermission.hasImport && (
                <SecondaryButton
                  icon={<IconFileUpload />}
                  dataTestId="file-upload-button"
                  iconSize={24}
                  onClick={() => setIsFileUploadDialogOpen(true)}
                  size="large"
                  text={formatMessage({
                    id: "label.file_upload",
                    defaultMessage: "Upload File",
                  })}
                  variant="secondary"
                />
            )}

          {!setupMode.includes("DASHBOARD") &&
            columnDefs.length > 0 &&
            permissions.setupPermission.hasManage && (
              <PrimaryButton
                icon={<IconAdd />}
                dataTestId="new-entry-button"
                iconSize={24}
                onClick={() => handleNewEntryClick(ctx)}
                size="large"
                text={getSetupBasedNewEntryText({ setupName, intl })}
                isDisabled={visibility.isNewEntryDisabled}
              />
            )}
        </StyledPageHeaderButtonContainer>
      )}
      {/* Record Page Actions */}
      {visibility.shouldShowRecordPageActions && (
        <StyledPageHeaderButtonContainer>
          {flags.isARRecordMigrationFeatureEnabled && (
            <TextButton
              dataTestId="convert-ar-schedule-type-button"
              icon={<IconSync />}
              onClick={() => handleConversionARRecordDialogOpen(ctx)}
              size="large"
              text={formatMessage({
                id: "button.migrate",
                defaultMessage: "Convert AR Schedule Type",
              })}
            />
          )}

          {visibility.isClearChangesButtonVisible && (
            <TextButton
              dataTestId="clear-changes-button"
              icon={<IconRefresh />}
              onClick={() => handleRecordReset(ctx)}
              size="large"
              text={formatMessage({
                id: "button.clear",
                defaultMessage: "Clear Changes",
              })}
              isDisabled={!visibility.isRecordChanged}
            />
          )}

          {visibility.shouldShowCancelOrCloseButton && (
            <SecondaryButton
              dataTestId={isReadOnlyRecord ? "close-button" : "cancel-button"}
              iconSize={24}
              onClick={() => handleCancelAndReset(ctx)}
              size="large"
              text={cancelOrCloseText}
            />
          )}

          {visibility.shouldShowSaveButton && (
            <PrimaryButton
              dataTestId="save-button"
              iconSize={24}
              onClick={() => handleSave(ctx)}
              size="large"
              text={formatMessage({
                id: "button.save",
                defaultMessage: "Save",
              })}
              isDisabled={visibility.isSaveDisabled}
            />
          )}

          {visibility.shouldShowSaveAllButton && (
            <PrimaryButton
              dataTestId="save-all-button"
              iconSize={24}
              onClick={() => handleSave(ctx)}
              size="large"
              text={formatMessage({
                id: "button.saveAll",
                defaultMessage: "Save All",
              })}
              isDisabled={Boolean(selectedTabSectionKey)}
            />
          )}

          {visibility.isARMigrationDialogOpen && <ARMigrationDialog />}
        </StyledPageHeaderButtonContainer>
      )}
    </StyledPageHeaderWrapper>
  );
}
